/**
 * Marketplace Filters Sidebar
 * UF-008: Agent Marketplace UI
 */

import { motion, AnimatePresence } from "framer-motion";
import {
  type AgentCategory,
  type PricingModel,
  type RatingFilter,
  type SortOption,
  type MarketplaceFilters as FiltersType,
  CATEGORY_CONFIG,
  PRICING_CONFIG,
  SORT_OPTIONS,
} from "./types";

// =============================================================================
// Types
// =============================================================================

interface MarketplaceFiltersProps {
  filters: FiltersType;
  onFiltersChange: (filters: FiltersType) => void;
  resultCount: number;
  isCollapsed?: boolean;
  onToggleCollapse?: () => void;
}

// =============================================================================
// Animation Variants
// =============================================================================

const sidebarVariants = {
  expanded: {
    width: 280,
    opacity: 1,
    transition: { duration: 0.3, ease: [0.25, 0.46, 0.45, 0.94] },
  },
  collapsed: {
    width: 0,
    opacity: 0,
    transition: { duration: 0.3, ease: [0.25, 0.46, 0.45, 0.94] },
  },
};

const sectionVariants = {
  hidden: { opacity: 0, y: 10 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: {
      delay: i * 0.1,
      duration: 0.4,
      ease: [0.25, 0.46, 0.45, 0.94],
    },
  }),
};

// =============================================================================
// Rating Star Component
// =============================================================================

function RatingStar({ filled, partial }: { filled: boolean; partial?: number }) {
  return (
    <svg
      className={`w-4 h-4 ${filled ? "text-hvme-gold" : "text-hvme-text-muted/30"}`}
      viewBox="0 0 24 24"
      fill="currentColor"
    >
      {partial !== undefined && partial > 0 && partial < 1 ? (
        <>
          <defs>
            <linearGradient id={`star-partial-${partial}`}>
              <stop offset={`${partial * 100}%`} stopColor="currentColor" />
              <stop offset={`${partial * 100}%`} stopColor="rgba(240,240,250,0.3)" />
            </linearGradient>
          </defs>
          <path
            fill={`url(#star-partial-${partial})`}
            d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"
          />
        </>
      ) : (
        <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
      )}
    </svg>
  );
}

function RatingStars({ rating, size = "sm" }: { rating: number; size?: "sm" | "md" }) {
  const fullStars = Math.floor(rating);
  const partialStar = rating - fullStars;

  return (
    <div className={`flex items-center gap-0.5 ${size === "md" ? "scale-110" : ""}`}>
      {[1, 2, 3, 4, 5].map((star) => (
        <RatingStar
          key={star}
          filled={star <= fullStars}
          partial={star === fullStars + 1 ? partialStar : undefined}
        />
      ))}
    </div>
  );
}

// =============================================================================
// Filter Checkbox Component
// =============================================================================

function FilterCheckbox({
  checked,
  onChange,
  label,
  count,
  color,
}: {
  checked: boolean;
  onChange: (checked: boolean) => void;
  label: string;
  count?: number;
  color?: string;
}) {
  return (
    <label className="flex items-center gap-3 cursor-pointer group">
      <div className="relative">
        <input
          type="checkbox"
          checked={checked}
          onChange={(e) => onChange(e.target.checked)}
          className="sr-only"
        />
        <motion.div
          className={`
            w-5 h-5 rounded border-2 transition-all duration-200
            flex items-center justify-center
            ${checked
              ? "bg-hvme-primary border-hvme-primary"
              : "border-hvme-border group-hover:border-hvme-border-hover"
            }
          `}
          whileTap={{ scale: 0.95 }}
        >
          <AnimatePresence>
            {checked && (
              <motion.svg
                initial={{ scale: 0, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0, opacity: 0 }}
                transition={{ duration: 0.15 }}
                className="w-3 h-3 text-white"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="3"
              >
                <path d="M5 13l4 4L19 7" />
              </motion.svg>
            )}
          </AnimatePresence>
        </motion.div>
      </div>
      <span className="flex items-center gap-2 text-sm text-hvme-text-secondary group-hover:text-hvme-text-primary transition-colors">
        {color && (
          <span
            className="w-2 h-2 rounded-full"
            style={{ backgroundColor: color }}
          />
        )}
        {label}
        {count !== undefined && (
          <span className="text-hvme-text-muted text-xs">({count})</span>
        )}
      </span>
    </label>
  );
}

// =============================================================================
// Rating Filter Option
// =============================================================================

function RatingFilterOption({
  minRating,
  selected,
  onSelect,
}: {
  minRating: number;
  selected: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      onClick={onSelect}
      className={`
        flex items-center gap-2 px-3 py-2 rounded-lg w-full
        transition-all duration-200
        ${selected
          ? "bg-hvme-primary/20 border border-hvme-primary/30"
          : "hover:bg-hvme-bg-mid border border-transparent"
        }
      `}
    >
      <RatingStars rating={minRating} />
      <span className="text-sm text-hvme-text-secondary">& up</span>
    </button>
  );
}

// =============================================================================
// Sort Dropdown
// =============================================================================

function SortDropdown({
  value,
  onChange,
}: {
  value: SortOption;
  onChange: (value: SortOption) => void;
}) {
  return (
    <div className="relative">
      <select
        value={value}
        onChange={(e) => onChange(e.target.value as SortOption)}
        className="
          w-full appearance-none bg-hvme-bg-deep border border-hvme-border rounded-lg
          px-4 py-2.5 pr-10 text-sm text-hvme-text-primary
          focus:outline-none focus:border-hvme-primary/40 focus:ring-1 focus:ring-hvme-primary/20
          transition-all duration-200 cursor-pointer
        "
      >
        {SORT_OPTIONS.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
      <svg
        className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-hvme-text-muted pointer-events-none"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
      >
        <path d="M6 9l6 6 6-6" />
      </svg>
    </div>
  );
}

// =============================================================================
// Filter Section Wrapper
// =============================================================================

function FilterSection({
  title,
  children,
  index,
}: {
  title: string;
  children: React.ReactNode;
  index: number;
}) {
  return (
    <motion.div
      custom={index}
      variants={sectionVariants}
      initial="hidden"
      animate="visible"
      className="space-y-3"
    >
      <h3 className="font-heading font-semibold text-sm text-hvme-text-primary uppercase tracking-wider">
        {title}
      </h3>
      {children}
    </motion.div>
  );
}

// =============================================================================
// Main Component
// =============================================================================

export function MarketplaceFilters({
  filters,
  onFiltersChange,
  resultCount,
  isCollapsed = false,
  onToggleCollapse,
}: MarketplaceFiltersProps) {
  const categories: AgentCategory[] = ["ml", "analytics", "data-processing", "custom"];
  const pricingModels: PricingModel[] = ["free", "paid", "subscription"];
  const ratingOptions: RatingFilter[] = [4, 3, 2, 1];

  const toggleCategory = (category: AgentCategory) => {
    const newCategories = filters.categories.includes(category)
      ? filters.categories.filter((c) => c !== category)
      : [...filters.categories, category];
    onFiltersChange({ ...filters, categories: newCategories });
  };

  const togglePricing = (model: PricingModel) => {
    const newModels = filters.pricingModels.includes(model)
      ? filters.pricingModels.filter((m) => m !== model)
      : [...filters.pricingModels, model];
    onFiltersChange({ ...filters, pricingModels: newModels });
  };

  const setRatingFilter = (rating: RatingFilter) => {
    onFiltersChange({
      ...filters,
      minRating: filters.minRating === rating ? null : rating,
    });
  };

  const clearFilters = () => {
    onFiltersChange({
      search: "",
      categories: [],
      pricingModels: [],
      minRating: null,
      sortBy: "popular",
    });
  };

  const hasActiveFilters =
    filters.categories.length > 0 ||
    filters.pricingModels.length > 0 ||
    filters.minRating !== null;

  return (
    <AnimatePresence>
      {!isCollapsed && (
        <motion.aside
          variants={sidebarVariants}
          initial="collapsed"
          animate="expanded"
          exit="collapsed"
          className="flex-shrink-0 overflow-hidden"
        >
          <div className="w-[280px] h-full p-6 space-y-8">
            {/* Header */}
            <div className="flex items-center justify-between">
              <div>
                <h2 className="font-heading font-bold text-lg text-hvme-text-primary">
                  Filters
                </h2>
                <p className="text-xs text-hvme-text-muted mt-1">
                  {resultCount} agents found
                </p>
              </div>
              {hasActiveFilters && (
                <motion.button
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.8 }}
                  onClick={clearFilters}
                  className="text-xs text-hvme-primary hover:text-hvme-accent transition-colors"
                >
                  Clear all
                </motion.button>
              )}
            </div>

            {/* Sort By */}
            <FilterSection title="Sort By" index={0}>
              <SortDropdown
                value={filters.sortBy}
                onChange={(sortBy) => onFiltersChange({ ...filters, sortBy })}
              />
            </FilterSection>

            {/* Categories */}
            <FilterSection title="Categories" index={1}>
              <div className="space-y-2">
                {categories.map((category) => (
                  <FilterCheckbox
                    key={category}
                    checked={filters.categories.includes(category)}
                    onChange={() => toggleCategory(category)}
                    label={CATEGORY_CONFIG[category].label}
                    color={CATEGORY_CONFIG[category].color}
                  />
                ))}
              </div>
            </FilterSection>

            {/* Pricing */}
            <FilterSection title="Pricing" index={2}>
              <div className="space-y-2">
                {pricingModels.map((model) => (
                  <FilterCheckbox
                    key={model}
                    checked={filters.pricingModels.includes(model)}
                    onChange={() => togglePricing(model)}
                    label={PRICING_CONFIG[model].label}
                  />
                ))}
              </div>
            </FilterSection>

            {/* Rating */}
            <FilterSection title="Rating" index={3}>
              <div className="space-y-1">
                {ratingOptions.filter((r): r is NonNullable<typeof r> => r !== null).map((rating) => (
                  <RatingFilterOption
                    key={rating}
                    minRating={rating}
                    selected={filters.minRating === rating}
                    onSelect={() => setRatingFilter(rating)}
                  />
                ))}
              </div>
            </FilterSection>

            {/* Mobile Close Button */}
            {onToggleCollapse && (
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={onToggleCollapse}
                className="
                  w-full py-3 rounded-xl font-body text-sm font-medium
                  bg-hvme-bg-mid border border-hvme-border
                  text-hvme-text-secondary hover:text-hvme-text-primary
                  transition-all duration-200 md:hidden
                "
              >
                Close Filters
              </motion.button>
            )}
          </div>
        </motion.aside>
      )}
    </AnimatePresence>
  );
}

export default MarketplaceFilters;
